/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2022, Zumper
 */

import { payloadReducer } from '@zcode/reducers'
import type { ForYou } from '@zcode/state-type'
import { filterActions } from '@zumper/redux-ignore'
import { combineReducers } from 'redux'
import { handleAction } from 'redux-actions'
import {
  RECEIVE_LISTINGS,
  RECEIVE_LOCATION,
  RECEIVE_NEARBY_HOODS,
  RECEIVE_PRICE_DATA,
  RECEIVE_REC_ENGINE_LISTINGS,
  RECEIVE_REC_ENGINE_SCORES,
  RECEIVE_SCORES,
  RECEIVE_TOP_NEARBY_HOODS,
  SET_LISTINGS_FETCHING,
  SET_LOCATION_URL,
  SET_NEARBY_HOODS_FETCHING,
  SET_REC_ENGINE_LISTINGS_FETCHING,
} from '../constants'
import type { ActionTypes } from '../types'
import { allowedList } from './allowedList'

const rootReducer = combineReducers<ForYou, ActionTypes>({
  locationUrl: handleAction<ForYou['locationUrl'], ActionTypes, typeof SET_LOCATION_URL>(
    SET_LOCATION_URL,
    payloadReducer,
    ''
  ),
  location: handleAction<ForYou['location'], ActionTypes, typeof RECEIVE_LOCATION>(
    RECEIVE_LOCATION,
    payloadReducer,
    {}
  ),
  priceData: handleAction<ForYou['priceData'], ActionTypes, typeof RECEIVE_PRICE_DATA>(
    RECEIVE_PRICE_DATA,
    payloadReducer,
    []
  ),
  listings: combineReducers<ForYou['listings'], ActionTypes>({
    listables: handleAction<ForYou['listings']['listables'], ActionTypes, typeof RECEIVE_LISTINGS>(
      RECEIVE_LISTINGS,
      payloadReducer,
      []
    ),
    fetching: handleAction<
      ForYou['listings']['fetching'],
      ActionTypes,
      typeof SET_LISTINGS_FETCHING
    >(SET_LISTINGS_FETCHING, payloadReducer, false),
  }),
  recEngineListings: combineReducers<ForYou['recEngineListings'], ActionTypes>({
    listables: handleAction<
      ForYou['recEngineListings']['listables'],
      ActionTypes,
      typeof RECEIVE_REC_ENGINE_LISTINGS
    >(RECEIVE_REC_ENGINE_LISTINGS, payloadReducer, []),
    fetching: handleAction<
      ForYou['recEngineListings']['fetching'],
      ActionTypes,
      typeof SET_REC_ENGINE_LISTINGS_FETCHING
    >(SET_REC_ENGINE_LISTINGS_FETCHING, payloadReducer, false),
  }),
  recEngineScores: handleAction<
    ForYou['recEngineScores'],
    ActionTypes,
    typeof RECEIVE_REC_ENGINE_SCORES
  >(RECEIVE_REC_ENGINE_SCORES, payloadReducer, []),
  scores: handleAction<ForYou['scores'], ActionTypes, typeof RECEIVE_SCORES>(
    RECEIVE_SCORES,
    payloadReducer,
    []
  ),
  nearbyHoods: combineReducers<ForYou['nearbyHoods'], ActionTypes>({
    hoods: handleAction<ForYou['nearbyHoods']['hoods'], ActionTypes, typeof RECEIVE_NEARBY_HOODS>(
      RECEIVE_NEARBY_HOODS,
      payloadReducer,
      []
    ),
    fetching: handleAction<
      ForYou['nearbyHoods']['fetching'],
      ActionTypes,
      typeof SET_NEARBY_HOODS_FETCHING
    >(SET_NEARBY_HOODS_FETCHING, payloadReducer, false),
  }),
  topNearbyHoods: handleAction<
    ForYou['topNearbyHoods'],
    ActionTypes,
    typeof RECEIVE_TOP_NEARBY_HOODS
  >(RECEIVE_TOP_NEARBY_HOODS, payloadReducer, []),
})

export const forYouReducer = filterActions(rootReducer, allowedList)
